<!-- Loading State -->
<div *ngIf="isLoading" class="flex flex-col items-center justify-center py-16">
  <div class="relative">
    <div class="animate-spin rounded-full h-16 w-16 border-4 border-red-100"></div>
    <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600 absolute top-0 left-0"></div>
  </div>
  <p class="mt-4 text-gray-600 font-medium animate-pulse">Cargando progreso...</p>
</div>

<!-- Error Message -->
<div
  *ngIf="errorMessage"
  class="mb-5 bg-red-50 border-l-4 border-red-500 text-red-800 px-5 py-4 rounded-lg shadow-sm flex items-start animate-shake"
>
  <svg class="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
  </svg>
  <span class="font-medium">{{ errorMessage }}</span>
</div>

<!-- Progress Content -->
<div *ngIf="!isLoading && progressData" class="space-y-6 animate-slide-in">
  <!-- Overall Stats -->
  <div class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
    <div class="flex items-center justify-between mb-5 flex-wrap gap-3">
      <h3 class="text-lg font-bold text-gray-900 tracking-tight">Resumen General</h3>
      <div class="flex items-center gap-3">
        <!-- License Selector -->
        <div *ngIf="licenseOptions.length > 0" class="flex items-center gap-2">
          <span class="text-sm text-gray-600 font-medium">Carnet:</span>
          <p-dropdown
            [options]="licenseOptions"
            [(ngModel)]="selectedLicenseId"
            (onChange)="onLicenseChange()"
            optionLabel="label"
            optionValue="value"
            [style]="{'min-width': '150px'}"
            placeholder="Seleccionar carnet"
          ></p-dropdown>
        </div>
        <span class="px-3 py-1 bg-gray-100 rounded-full text-xs font-bold text-gray-700 border border-gray-200">
          {{ getSelectedLicenseStats().total }} Evaluaciones
        </span>
      </div>
    </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Conseguido -->
        <div class="bg-white rounded-xl p-5 shadow-sm border-2 border-green-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
          <div class="flex items-center justify-between mb-3">
            <span class="text-3xl group-hover:scale-110 transition-transform duration-300">{{ ratingEmojis['conseguido'] }}</span>
            <div class="px-3 py-1 bg-green-100 rounded-full">
              <span class="text-xs font-bold text-green-700">{{ getRatingPercentage('conseguido') }}%</span>
            </div>
          </div>
          <div class="text-xs font-bold text-green-700 mb-1 uppercase tracking-wide">
            {{ ratingLabels['conseguido'] }}
          </div>
          <div class="text-3xl font-bold text-green-600">
            {{ getSelectedLicenseStats().conseguido }}
          </div>
        </div>

        <!-- En Progreso -->
        <div class="bg-white rounded-xl p-5 shadow-sm border-2 border-yellow-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
          <div class="flex items-center justify-between mb-3">
            <span class="text-3xl group-hover:scale-110 transition-transform duration-300">{{ ratingEmojis['en_progreso'] }}</span>
            <div class="px-3 py-1 bg-yellow-100 rounded-full">
              <span class="text-xs font-bold text-yellow-700">{{ getRatingPercentage('en_progreso') }}%</span>
            </div>
          </div>
          <div class="text-xs font-bold text-yellow-700 mb-1 uppercase tracking-wide">
            {{ ratingLabels['en_progreso'] }}
          </div>
          <div class="text-3xl font-bold text-yellow-600">
            {{ getSelectedLicenseStats().en_progreso }}
          </div>
        </div>

        <!-- Necesita Mejorar -->
        <div class="bg-white rounded-xl p-5 shadow-sm border-2 border-red-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
          <div class="flex items-center justify-between mb-3">
            <span class="text-3xl group-hover:scale-110 transition-transform duration-300">{{ ratingEmojis['necesita_mejorar'] }}</span>
            <div class="px-3 py-1 bg-red-100 rounded-full">
              <span class="text-xs font-bold text-red-700">{{ getRatingPercentage('necesita_mejorar') }}%</span>
            </div>
          </div>
          <div class="text-xs font-bold text-red-700 mb-1 uppercase tracking-wide">
            {{ ratingLabels['necesita_mejorar'] }}
          </div>
          <div class="text-3xl font-bold text-red-600">
            {{ getSelectedLicenseStats().necesita_mejorar }}
          </div>
        </div>
      </div>
  </div>

  <!-- Skills by License -->
  <div class="space-y-6">
    <div class="flex items-center justify-between flex-wrap gap-4">
      <h3 class="text-lg font-bold text-gray-900 tracking-tight">Progreso por Habilidades</h3>
    </div>

    <!-- Loop through filtered licenses -->
    <div *ngFor="let licenseGroup of getFilteredLicenseGroups(); let i = index" class="space-y-4">
      <!-- License Header -->
      <div class="flex items-center gap-3">
        <div class="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg shadow-sm">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
          </svg>
          <span class="font-bold text-sm">{{ licenseGroup.license?.name || 'Sin carnet' }}</span>
        </div>
        <span class="text-gray-500 text-sm">
          {{ licenseGroup.skills_progress.length }} habilidades evaluadas
        </span>
      </div>

      <!-- Categories within this license -->
      <div *ngFor="let category of getCategoriesForLicense(licenseGroup)"
           class="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 ml-4">
        <!-- Category Header -->
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-3 border-b-2 border-gray-200">
          <div class="flex items-center gap-3">
            <span
              class="px-4 py-1.5 rounded-full text-sm font-bold shadow-sm border-2"
              [ngClass]="getCategoryColor(category)"
            >
              {{ getCategoryLabel(category) }}
            </span>
            <span class="text-gray-600 text-sm font-medium">
              {{ getSkillsByCategoryForLicense(licenseGroup, category).length }} habilidades
            </span>
          </div>
        </div>

        <!-- Skills List -->
        <div class="divide-y-2 divide-gray-100">
          <div
            *ngFor="let skillProgress of getSkillsByCategoryForLicense(licenseGroup, category)"
            class="px-6 py-5 hover:bg-gray-50 transition-all duration-200 group"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0">
                <h4 class="font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                  {{ skillProgress.skill.name }}
                </h4>
                <p class="text-sm text-gray-600 mt-1.5 leading-relaxed">{{ skillProgress.skill.description }}</p>

                <!-- Latest Rating Badge -->
                <div class="mt-3 flex items-center gap-3 flex-wrap">
                  <span
                    class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold border-2 shadow-sm"
                    [ngClass]="getRatingColor(skillProgress.latest_rating)"
                  >
                    <span class="text-base mr-1.5">{{ getRatingEmoji(skillProgress.latest_rating) }}</span>
                    {{ getRatingLabel(skillProgress.latest_rating) }}
                  </span>
                  <span class="text-xs text-gray-500 font-medium px-3 py-1 bg-gray-100 rounded-full">
                    {{ skillProgress.history_count }} evaluación{{ skillProgress.history_count !== 1 ? 'es' : '' }}
                  </span>
                </div>
              </div>

              <!-- View History Button -->
              <button
                *ngIf="skillProgress.history_count > 0"
                (click)="viewSkillHistory(skillProgress.skill.id)"
                class="flex-shrink-0 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 flex items-center gap-2 border-2 border-transparent hover:border-red-200 group-hover:scale-105"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Historial
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Empty State -->
<div *ngIf="!isLoading && !progressData" class="text-center py-20">
  <div class="relative inline-block">
    <div class="absolute inset-0 bg-red-100 rounded-full blur-3xl opacity-40 animate-pulse"></div>
    <svg class="relative mx-auto h-24 w-24 text-red-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  </div>
  <h3 class="mt-6 text-xl font-bold text-gray-900">Sin evaluaciones aún</h3>
  <p class="mt-2 text-sm text-gray-600 max-w-md mx-auto">Este alumno aún no tiene evaluaciones de habilidades registradas. Las evaluaciones aparecerán aquí una vez que se realicen.</p>
</div>

